import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { getApiErrorMessage } from '../../components/ui/FormControls';

export interface Shift {
  id: string;
  code: string;
  name: string;
  startTime: string;
  endTime: string;
  breakMinutes: number;
  graceMinutes: number;
  workMinutes: number;
  isActive: boolean;
}

export interface Contractor {
  id: string;
  code: string;
  name: string;
  licenceNumber: string | null;
  licenceValidUntil: string | null;
  contactPerson: string | null;
  phone: string | null;
  ratePerManDay: string | null;
  ratePerOvertimeHour: string | null;
  isActive: boolean;
  workers: number;
}

export interface PayComponent {
  id: string;
  code: string;
  name: string;
  kind: 'earning' | 'deduction' | 'employer';
  method: 'fixed' | 'percent' | 'slab';
  value: string;
  base: 'basic' | 'gross';
  ceiling: string | null;
  eligibleUpToGross: string | null;
  slabs: { upTo: number | null; amount: number }[] | null;
  prorate: boolean;
  appliesTo: 'all' | 'monthly' | 'daily';
  sequence: number;
  isActive: boolean;
}

export const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
export const tdClass = 'px-4 py-2.5 text-sm text-gray-700';

export const localToday = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
export const lastMonth = () => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${first.getFullYear()}-${String(first.getMonth() + 1).padStart(2, '0')}`;
};
export const monthName = (month: string) => new Date(`${month}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
export const day = (value?: string | null) => (value ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
export const hours = (minutes: number) => `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;

export const useShifts = () => useQuery<Shift[]>({ queryKey: ['hr', 'shifts'], queryFn: async () => (await api.get('/hr/shifts')).data });
export const useContractors = () => useQuery<Contractor[]>({ queryKey: ['hr', 'contractors'], queryFn: async () => (await api.get('/hr/contractors')).data });
export const useComponents = () => useQuery<PayComponent[]>({ queryKey: ['hr', 'components'], queryFn: async () => (await api.get('/hr/pay-components')).data });

export const failed = (fallback: string) => (error: unknown) => toast.error(getApiErrorMessage(error, fallback), { duration: 8000 });

// A file from the server, saved under the name it gives; a failure's message is read out of the blob
export const download = async (url: string, fallback: string) => {
  try {
    const response = await api.get(url, { responseType: 'blob' });
    const named = /filename="?([^";]+)"?/.exec(String(response.headers['content-disposition'] ?? ''));
    const link = document.createElement('a');
    link.href = URL.createObjectURL(response.data as Blob);
    link.download = named?.[1] ?? fallback;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    const data = (error as { response?: { data?: unknown } })?.response?.data;
    let message = 'The file could not be made';
    if (data instanceof Blob) {
      try {
        message = JSON.parse(await data.text()).message || message;
      } catch {
        // keep the general message
      }
    }
    toast.error(message, { duration: 8000 });
  }
};
